import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Row,
  Col,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  PhoneOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import {
  whatsappConnectorService,
  WhatsAppAccountMapping,
  WhatsAppAccount,
  AgentPlatform,
  CreateAccountMappingRequest,
  UpdateAccountMappingRequest
} from '../services/whatsappConnectorService';
import { agentService, Agent } from '../services/agentService';
import { serverService } from '../services/serverService';
import { api } from '../services/authService';
import TimeDisplay from './TimeDisplay';
import { useI18n } from '../hooks/useI18n';
import './WhatsAppConnectorManager.css';

const { Option } = Select;
const { TextArea } = Input;

const AccountMappingsPage: React.FC = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState<WhatsAppAccountMapping[]>([]);
  const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
  const [platforms, setPlatforms] = useState<AgentPlatform[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [services, setServices] = useState<unknown[]>([]);
  const [proxiedAgentPlatforms, setProxiedAgentPlatforms] = useState<unknown[]>([]);
  const [fileStorageProviders, setFileStorageProviders] = useState<unknown[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState<WhatsAppAccountMapping | null>(null);
  const [form] = Form.useForm();
  const { t } = useI18n();



  // 加载数据
  const loadData = async () => {
    setLoading(true);
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      const [mappingsRes, accountsRes, platformsRes, agentsRes, servicesRes, proxiedAgentPlatformsRes, allServicesRes] = await Promise.all([
        whatsappConnectorService.getAccountMappings(),
        whatsappConnectorService.getWhatsAppAccounts(),
        whatsappConnectorService.getAgentPlatforms(),
        agentService.getAgents({ tenant_code: tenantCode }),
        serverService.getServices({ tenant_code: tenantCode, service_type: 'ai_platform' }),
        serverService.getServices({ tenant_code: tenantCode, service_type: 'proxied_ai_platform' as any }),
        // 获取所有服务，用于文件存储提供商选择
        serverService.getServices({ tenant_code: tenantCode }).catch(() => ({ data: [] }))
      ]);

      setMappings(mappingsRes.data || []);
      setAccounts(accountsRes.data || []);
      setPlatforms(platformsRes.data || []);
      setAgents(agentsRes.data || []);
      setServices(servicesRes.data || []);
      setProxiedAgentPlatforms(proxiedAgentPlatformsRes.data || []);
      
      // 使用所有服务作为文件存储提供商的选项
      const allServices = allServicesRes.data || [];
      console.log('All Services for File Storage Provider:', allServices);
      setFileStorageProviders(allServices);
    } catch (error: any) {
      console.error('加载数据失败:', error);
      message.error(t('mappings.loadFailed') + ': ' + (error.message || t('common.error')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // 表格列配置
  const columns = [
    {
      title: t('mappings.whatsappAccount'),
      dataIndex: 'whatsapp_account_id',
      key: 'whatsapp_account_id',
      width: 200,
      render: (accountId: number) => {
        const account = accounts.find(acc => acc.id === accountId);
        return account ? (
          <Space>
            <PhoneOutlined />
            <div>
              <div>{account.account_name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{account.phone_number}</div>
            </div>
          </Space>
        ) : `${t('mappings.agentId')}: ${accountId}`;
      },
    },
    {
      title: '关联服务',
      dataIndex: 'service_name',
      key: 'service_name',
      width: 220,
      render: (serviceName: string, record: WhatsAppAccountMapping) => {
        if (!serviceName) return <Tag color="default">未关联</Tag>;
        const isProxy = (record.platform_type || '').endsWith('_proxy') || /_proxy$/i.test(serviceName || '');
        return (
          <Space size={4}>
            <Tooltip title={`平台: ${record.platform_type || '未知'}`}>
              <Tag color="blue">{serviceName}</Tag>
            </Tooltip>
            {isProxy && (record as any).proxied_service_name ? (
              <Tooltip title="被代理的服务 (proxied service)">
                <Tag color="gold">→ {(record as any).proxied_service_name}</Tag>
              </Tooltip>
            ) : null}
            {(record as any).file_storage_provider_service_name ? (
              <Tooltip title="文件存储提供商服务 (File Storage Provider Service)">
                <Tag color="green">📁 {(record as any).file_storage_provider_service_name}</Tag>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Agent',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 150,
      render: (agentName: string, record: WhatsAppAccountMapping) => {
        if (!agentName) return <Tag color="default">未关联</Tag>;
        return (
          <Space>
            <RobotOutlined />
            <Tag color="purple">{agentName}</Tag>
          </Space>
        );
      },
    },
    {
      title: t('mappings.processingMode'),
      dataIndex: 'processing_mode',
      key: 'processing_mode',
      width: 100,
      render: (mode: string) => (
        <Tag color={mode === 'sync' ? 'blue' : 'orange'}>
          {mode === 'sync' ? t('mappings.sync') : t('mappings.async')}
        </Tag>
      ),
    },
    {
      title: t('mappings.timeout'),
      dataIndex: 'timeout_seconds',
      key: 'timeout_seconds',
      width: 100,
      render: (seconds: number) => `${seconds}s`,
    },
    {
      title: t('mappings.maxRetries'),
      dataIndex: 'max_retries',
      key: 'max_retries',
      width: 100,
    },
    {
      title: t('common.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => (
        <Badge
          status={active ? 'success' : 'error'}
          text={active ? t('common.active') : t('common.inactive')}
        />
      ),
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: t('common.updatedAt'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: t('common.edit'),
      key: 'action',
      width: 150,
      render: (_: any, record: WhatsAppAccountMapping) => (
        <Space size="small">
          <Tooltip title={t('common.edit')}>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <Popconfirm
              title={t('mappings.confirmDelete')}
              onConfirm={() => handleDelete(record.id)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 处理函数
  const handleCreate = async (values: any) => {
    try {
      // 处理空字符串，将空字符串转换为 undefined
      const processedValues = Object.entries(values).reduce((acc, [key, value]) => {
        if (value === '' || value === null) {
          acc[key] = undefined;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const data: CreateAccountMappingRequest = {
        tenant_code: localStorage.getItem('currentTenant') || 'default',
        ...processedValues,
      };
      await whatsappConnectorService.createAccountMapping(data);
      message.success(t('mappings.createSuccess'));
      setModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error: any) {
      message.error(t('mappings.createFailed') + ': ' + error.message);
    }
  };

  const handleEdit = (mapping: WhatsAppAccountMapping) => {
    setEditingMapping(mapping);
    form.setFieldsValue(mapping);
    setModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    if (!editingMapping) return;

    try {
      // 处理空字符串，将空字符串转换为 undefined
      const processedValues = Object.entries(values).reduce((acc, [key, value]) => {
        if (value === '' || value === null) {
          acc[key] = undefined;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const data: UpdateAccountMappingRequest = processedValues;
      await whatsappConnectorService.updateAccountMapping(editingMapping.id, data);
      message.success(t('mappings.updateSuccess'));
      setModalVisible(false);
      setEditingMapping(null);
      form.resetFields();
      loadData();
    } catch (error: any) {
      message.error(t('mappings.updateFailed') + ': ' + error.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await whatsappConnectorService.deleteAccountMapping(id);
      message.success(t('mappings.deleteSuccess'));
      loadData();
    } catch (error: any) {
      message.error(t('mappings.deleteFailed') + ': ' + error.message);
    }
  };

  return (
    <div className="whatsapp-connector-container">
      <Card className="whatsapp-connector-card">
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: '#faad14' }}>{t('mappings.title')}</h2>
            <p style={{ margin: '8px 0 0 0', color: '#666' }}>
              {t('mappings.subtitle')}
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingMapping(null);
              form.resetFields();
              setModalVisible(true);
            }}
            className="whatsapp-connector-button-primary"
          >
            {t('mappings.createMapping')}
          </Button>
        </div>

        {accounts.length === 0 || platforms.length === 0 ? (
          <Alert
            message={t('mappings.missingConfig')}
            description={
              accounts.length === 0 && platforms.length === 0
                ? t('mappings.missingConfigDesc')
                : accounts.length === 0
                ? t('mappings.missingAccountDesc')
                : t('mappings.missingPlatformDesc')
            }
            type="warning"
            showIcon
            className="whatsapp-connector-alert"
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <Table
          columns={columns}
          dataSource={mappings}
          rowKey="id"
          loading={loading}
          className="whatsapp-connector-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${t('common.total')} ${total} ${t('common.items')}`,
            pageSize: 10,
          }}
          scroll={{ x: 1400 }}
        />

        {mappings.length === 0 && !loading && accounts.length > 0 && platforms.length > 0 && (
          <div className="whatsapp-connector-empty">
            <LinkOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
            <p style={{ margin: '16px 0', color: '#999' }}>{t('mappings.noMappings')}</p>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingMapping(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              {t('mappings.createFirstMapping')}
            </Button>
          </div>
        )}
      </Card>

      {/* 新建/编辑模态框 */}
      <Modal
        title={
          <Space>
            <LinkOutlined />
            {editingMapping ? t('mappings.modalTitle') : t('mappings.modalTitleNew')}
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingMapping(null);
          form.resetFields();
        }}
        footer={null}
        width={900}
        className="whatsapp-connector-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(vals) => {
            const latest = form.getFieldsValue(true);
            console.log('AccountMappings submit values:', vals);
            console.log('AccountMappings latest form values:', latest);
            return (editingMapping ? handleUpdate : handleCreate)(latest);
          }}
          className="whatsapp-connector-form"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="whatsapp_account_id"
                label={t('mappings.whatsappAccount')}
                rules={[{ required: true, message: t('mappings.selectWhatsappAccount') }]}
              >
                <Select placeholder={t('mappings.selectWhatsappAccount')}>
                  {accounts.map(account => (
                    <Option key={account.id} value={account.id}>
                      {account.account_name} ({account.phone_number})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="service_id"
                label="关联服务"
                rules={[{ required: true, message: '请选择关联的服务' }]}
              >
                <Select
                  placeholder="请选择 AI Platform 服务"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {services.map((service: any) => (
                    <Option key={service.id} value={service.id}>
                      {service.service_name} ({service.platform_type})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {/* 当选择的服务为 *_proxy 时，显示被代理服务选择 */}
            <Col span={12}>
              <Form.Item shouldUpdate={(prev, curr) => prev.service_id !== curr.service_id} noStyle>
                {() => {
                  const selectedService = (services as any[]).find((s: any) => s && s.id === form.getFieldValue('service_id')) as any | undefined;
                  const platformType: string = selectedService?.platform_type || '';
                  const serviceName: string = selectedService?.service_name || '';
                  const isProxy = !!selectedService && (platformType === 'ibuild_proxy' || /_proxy$/i.test(serviceName));
                  if (!isProxy) {
                    if (form.getFieldValue('proxied_service')) {
                      form.setFieldsValue({ proxied_service: undefined });
                    }
                    return null;
                  }
                  return (
                    <Form.Item
                      name="proxied_service"
                      label="被代理的服务 (proxied service)"
                    >
                      <Select
                        placeholder="请选择被代理的服务（可选）"
                        showSearch
                        allowClear
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {(proxiedAgentPlatforms as any[])
                          .filter((svc: any) => 
                            svc 
                            && svc.id !== form.getFieldValue('service_id')
                            && (svc.service_type === 'proxied_ai_platform')
                          )
                          .map((svc: any) => (
                            <Option key={svc.id} value={svc.id}>
                              {svc.service_name} ({svc.platform_type})
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agent_id"
                label="关联 Agent"
                rules={[{ required: true, message: '请选择关联的 Agent' }]}
              >
                <Select
                  placeholder="请选择 Agent"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {agents.map(agent => (
                    <Option key={agent.id} value={agent.id}>
                      {agent.agent_name} ({agent.agent_type})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="file_storage_provider_service"
                label="文件存储提供商服务 (File Storage Provider Service)"
              >
                <Select
                  placeholder="请选择文件存储提供商服务（可选）"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {(fileStorageProviders as any[])
                    .filter((provider: any) => provider && provider.is_active !== false)
                    .map((provider: any) => (
                      <Option key={provider.id} value={provider.id}>
                        {provider.service_name || provider.provider_name} ({provider.provider_type || provider.service_type})
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="processing_mode"
                label={t('mappings.processingMode')}
                rules={[{ required: true, message: t('mappings.selectProcessingMode') }]}
              >
                <Select placeholder={t('mappings.selectProcessingMode')}>
                  <Option value="sync">
                    <Space>
                      <CheckCircleOutlined style={{ color: '#1890ff' }} />
                      {t('mappings.sync')}
                    </Space>
                  </Option>
                  <Option value="async">
                    <Space>
                      <ClockCircleOutlined style={{ color: '#faad14' }} />
                      {t('mappings.async')}
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="timeout_seconds"
                label={t('mappings.timeoutSeconds')}
                rules={[{ required: true, message: t('mappings.enterTimeout') }]}
              >
                <Select placeholder={t('mappings.selectTimeout')}>
                  <Option value={30}>30{t('common.seconds')}</Option>
                  <Option value={60}>1{t('common.minute')}</Option>
                  <Option value={120}>2{t('common.minutes')}</Option>
                  <Option value={300}>5{t('common.minutes')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="max_retries"
                label={t('mappings.maxRetries')}
                rules={[{ required: true, message: t('mappings.selectMaxRetries') }]}
              >
                <Select placeholder={t('mappings.selectMaxRetries')}>
                  <Option value={0}>0{t('common.times')}</Option>
                  <Option value={1}>1{t('common.times')}</Option>
                  <Option value={3}>3{t('common.times')}</Option>
                  <Option value={5}>5{t('common.times')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="webhook_url"
            label={t('mappings.webhookUrl')}
            extra={t('mappings.webhookUrlExtra')}
          >
            <Input placeholder={t('mappings.enterWebhookUrl')} />
          </Form.Item>

          <Form.Item
            name="agent_config"
            label={t('mappings.agentConfig')}
            extra={t('mappings.agentConfigExtra')}
          >
            <TextArea
              rows={4}
              placeholder={t('mappings.enterAgentConfig')}
              showCount
              maxLength={2000}
            />
          </Form.Item>

          <Form.Item name="is_active" label={t('mappings.isActive')} valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingMapping(null);
                  form.resetFields();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingMapping ? t('common.save') : t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountMappingsPage;
